import React, { useState, useEffect, useMemo } from 'react';
import { Button, Alert } from 'react-bootstrap';
import {
    IconButton,
    TextField,
    Tooltip,
    CircularProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TanstackTable from 'components/elements/advance-table/TanstackTable';
import RefreshButton from 'components/elements/refresh-button/RefreshButton';
import { getUploadedResorucesAPI,getSignedUrl } from 'ServerAPI/serverAPI';
import actionIcon from "../../assets/images/course/action-icon.svg";
import { useToast } from 'context/toast-context/ToastProvider';
import { DateDisplay } from 'helper/utils';
import { useNavigate } from 'react-router-dom';
import { Download, Eye } from "lucide-react";
import { useCookies } from 'react-cookie';
import ConfirmationDialog from 'components/Dialog/ConfirmationDialog';   // <-- ADD THIS IMPORT

export const ResourceGenerationTable = ({ userId, setResourceCount }) => {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const [cookies] = useCookies(['userId']);

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [loadingViewId, setLoadingViewId] = useState(null);
    const [deleteDialog, setDeleteDialog] = useState({
  open: false,
  resourceId: null,
  fileName: null,
  organizationId: null,
  loading: false,
});
    // Safe wrapper
    const safeSetCount = typeof setResourceCount === 'function' ? setResourceCount : () => {};

    /* ------------------------------------------------------------------ */
    /*  FETCH RESOURCES                                                   */
    /* ------------------------------------------------------------------ */
    const fetchData = async (isRefresh = false) => {
        if (!userId) {
            showToast('Organization ID missing', 'error');
            setLoading(false);
            return;
        }

        try {
            if (isRefresh) setIsRefreshing(true);
            else setLoading(true);
            setError(null);

            const response = await getUploadedResorucesAPI(userId);
            console.log('DEBUG: Resources API response:', response);
            
            // Handle different response structures
            const resources = response.data?.resources || response.data?.content || [];
            console.log('DEBUG: Extracted resources:', resources);

            const mappedData = resources.map(item => ({
                resourceId: item.fileId,
                title: item.contentFileName?.replace(/\.pdf$/i, '') || 'Untitled Resource',
                status: item.vectorSpace === true ? 'completed' : 'processing',
                createdAt: item.createdDate,
                fileName: item.contentFileName,
                organizationId: item.organizationId
            }));

            console.log('DEBUG: Mapped data:', mappedData);
            setData(mappedData);
            safeSetCount(mappedData.length);

        } catch (err) {
            console.error('Error fetching resources:', err);
            const msg = err.response?.data?.message || err.message || 'Failed to load resources';
            setError(msg);
            showToast(msg, 'error');
            setData([]);
            safeSetCount(0);
        } finally {
            setLoading(false);
            setIsRefreshing(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [userId]);

    const handleRefresh = () => fetchData(true);
    const handleSearch = e => setSearchTerm(e.target.value);

    const handleUploadNewResource = () => {
        navigate('/resource-generation/');
    };
    const handleUploadNotes = () => {
        navigate('/notes-upload/');
    };

    /* ------------------------------------------------------------------ */
    /*  VIEW RESOURCE (NEW FUNCTIONALITY)                                */
    /* ------------------------------------------------------------------ */
    const handleViewResource = async (resourceId, fileName) => {
        try {
            console.log('DEBUG: Attempting to view resource:', resourceId);
            setLoadingViewId(resourceId);
            showToast('Opening resource...', 'info');

            const payload = {
                resources: true,
                userId: cookies.userId,
                resourceId: resourceId, // This should match the fileId from your resources
                fileName: fileName,
                resourcefileName: fileName,
                method: "GET"
            };

            console.log('DEBUG: Payload for getSignedUrl:', payload);

            const response = await getSignedUrl(payload);
            console.log('DEBUG: getSignedUrl response:', response);

            // Handle the response structure from your Lambda
            const preSignedUrl = response.data?.preSignedUrl;
            const objectUrl = response.data?.objectUrl;
            
            const viewUrl = preSignedUrl || objectUrl;
            
            if (viewUrl) {
                console.log('DEBUG: Opening URL:', viewUrl);
                // Open in new tab for viewing
                window.open(viewUrl, '_blank', 'noopener,noreferrer');
                showToast('Resource opened in new tab', 'success');
            } else {
                throw new Error('No viewable URL received');
            }

        } catch (err) {
            console.error('View resource error:', err);
            const errorMsg = err.response?.data?.error || err.message || 'Failed to open resource';
            showToast(errorMsg, 'error');
        } finally {
            setLoadingViewId(null);
        }
    };

    /* ------------------------------------------------------------------ */
    /*  DOWNLOAD RESOURCE                                                */
    /* ------------------------------------------------------------------ */
    const handleDownloadResource = async (resourceId, fileName) => {
        try {
            showToast('Preparing download...', 'info');

            const payload = {
                resources: true,
                userId: cookies.userId,
                resourceId: resourceId,
                fileName: fileName,
                method: "GET",
                resourcefileName: fileName,
            };

            const response = await getSignedUrl(payload);
            const downloadUrl = response.data?.preSignedUrl || response.data?.objectUrl;

            if (downloadUrl) {
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.download = fileName || 'resource.pdf';
                link.target = '_blank'; // Open in new tab for PDFs
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                showToast('Download started!', 'success');
            } else {
                throw new Error('No download URL');
            }
        } catch (err) {
            console.error('Download error:', err);
            showToast('Failed to download file', 'error');
        }
    };
/* ------------------------------------------------------------------ */
/*  DELETE RESOURCE – WITH CONFIRMATION DIALOG                        */
/* ------------------------------------------------------------------ */
const openDeleteDialog = (resourceId, fileName, organizationId) => {
  setDeleteDialog({
    open: true,
    resourceId,
    fileName,
    organizationId,
    loading: false,
  });
};

const closeDeleteDialog = () => {
  setDeleteDialog(prev => ({ ...prev, open: false, loading: false }));
};

const confirmDelete = async () => {
  const { resourceId, fileName, organizationId } = deleteDialog;
  setDeleteDialog(prev => ({ ...prev, loading: true }));

  try {
    // 1. S3 delete
    const payload = {
      resources: true,
      userId: cookies.userId,
      resourceId,
      fileName,
      resourcefileName: fileName,
      method: "DELETE"
    };
    await getSignedUrl(payload);

    // 2. Vector delete
    const { deleteFileVectors } = await import('ServerAPI/serverAPI');
    await deleteFileVectors(organizationId, fileName, null).catch(err =>
      console.error('Vector delete failed:', err)
    );

    // 3. Update UI
    setData(prev => prev.filter(r => r.resourceId !== resourceId));
    safeSetCount(prev => prev - 1);
    showToast('Resource deleted successfully', 'success');
    closeDeleteDialog();
  } catch (err) {
    console.error('Delete failed:', err);
    showToast('Failed to delete resource', 'error');
    setDeleteDialog(prev => ({ ...prev, loading: false }));
  }
};
    /* ------------------------------------------------------------------ */
    /*  FILTER & COLUMNS                                                 */
    /* ------------------------------------------------------------------ */
    const filteredData = useMemo(() => {
        if (!searchTerm) return data;
        return data.filter(item =>
            item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.status?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.fileName?.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [data, searchTerm]);

    const columns = useMemo(() => [
        {
            id: 'sno',
            header: 'S.No.',
            cell: ({ row }) => row.index + 1,
            enableSorting: false,
        },
        {
            accessorKey: 'title',
            header: 'Title',
            cell: ({ getValue }) => (
                <div className="fw-semibold text-truncate" style={{ maxWidth: '200px' }}>
                    {getValue() || 'Untitled'}
                </div>
            ),
        },
        {
            accessorKey: 'fileName',
            header: 'File Name',
            cell: ({ getValue }) => (
                <div className="text-muted text-truncate" style={{ maxWidth: '200px' }}>
                    {getValue() || 'No file name'}
                </div>
            ),
        },
        {
            accessorKey: 'status',
            header: 'Status',
            cell: ({ getValue }) => {
                const status = getValue();
                const colors = {
                    completed: 'success',
                    processing: 'warning',
                };
                return (
                    <span className={`badge bg-${colors[status] || 'secondary'}`}>
                        {status === 'completed' ? 'Ready' : 'Processing'}
                    </span>
                );
            },
        },

        {
            accessorKey: 'actions',
            header: 'Actions',
            cell: ({ row }) => {
                const resource = row.original;
                const isCompleted = resource.status === 'completed';
                const isLoading = loadingViewId === resource.resourceId;

                return (
                    <div className="d-flex gap-1">
                        {/* View Button */}
                        <Tooltip title={isCompleted ? "View PDF" : "Processing - not available yet"}>
                            <span>
                                <IconButton
                                    onClick={() => handleViewResource(resource.resourceId, resource.fileName)}
                                    disabled={!isCompleted || isLoading}
                                    size="small"
                                >
                                    {isLoading ? (
                                        <CircularProgress size={20} />
                                    ) : (
                                        <VisibilityIcon 
                                            fontSize="small" 
                                            color={isCompleted ? "primary" : "disabled"}
                                        />
                                    )}
                                </IconButton>
                            </span>
                        </Tooltip>

                        {/* Download Button */}
                        <Tooltip title={isCompleted ? "Download PDF" : "Processing - not available yet"}>
                            <span>
                                <IconButton
                                    onClick={() => handleDownloadResource(resource.resourceId, resource.fileName)}
                                    disabled={!isCompleted}
                                    size="small"
                                >
                                    <Download
                                        color={isCompleted ? "rgb(34, 77, 153)" : "#aaa"}
                                        size={20}
                                    />
                                </IconButton>
                            </span>
                        </Tooltip>
{/* DELETE – WITH DIALOG */}
<Tooltip title="Delete Resource">
  <span>
    <IconButton
      onClick={() => openDeleteDialog(resource.resourceId, resource.fileName, resource.organizationId)}
      size="small"
      color="error"
    >
      <i className="fas fa-trash-alt" />
    </IconButton>
  </span>
</Tooltip>
                    </div>
                );
            },
            enableSorting: false,
        },
    ], [loadingViewId]);

    /* ------------------------------------------------------------------ */
    /*  RENDER                                                            */
    /* ------------------------------------------------------------------ */
    if (loading && !isRefreshing) {
        return (
            <div className="text-center p-5">
                <CircularProgress />
                <div className="mt-2">Loading resources...</div>
            </div>
        );
    }

    return (
        <>
            {/* Search + Action Bar */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-4">
                <TextField
                    size="small"
                    variant="outlined"
                    placeholder="Search by title, file name, or status"
                    InputProps={{ startAdornment: <SearchIcon /> }}
                    onChange={handleSearch}
                    value={searchTerm}
                    className="w-100"
                    style={{ maxWidth: '400px' }}
                />
                <div className="d-flex align-items-center gap-2 flex-nowrap">
                    <RefreshButton
                        handleRefresh={handleRefresh}
                        title="Refresh Resources"
                        isRefreshing={isRefreshing}
                    />
                    <Button
                        variant="primary"
                        onClick={handleUploadNewResource}
                        className="d-flex align-items-center gap-1"
                    >
                        <AddIcon fontSize="small" />
                        Upload New Resource
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleUploadNotes}
                        className="d-flex align-items-center gap-1"
                    >
                        <AddIcon fontSize="small" />
                        Digitize Notes
                    </Button>
                </div>
            </div>

            {/* Error */}
            {error && (
                <Alert variant="danger" className="mb-4">
                    <Alert.Heading>Failed to Load Resources</Alert.Heading>
                    <p className="mb-0">{error}</p>
                    <hr />
                    <div className="d-flex justify-content-end">
                        <Button variant="outline-danger" size="sm" onClick={() => fetchData()}>
                            Retry
                        </Button>
                    </div>
                </Alert>
            )}

            {/* Table / Empty */}
            <div className="mt-3">
                {!error && (
                    <>
                        {filteredData.length === 0 ? (
                            <div className="text-center p-5">
                                <div className="mb-3">
                                    <i className="fas fa-file-pdf fa-3x text-muted"></i>
                                </div>
                                <h5 className="text-muted">
                                    {searchTerm ? 'No matching resources' : 'No resources uploaded yet'}
                                </h5>
                                <p className="text-muted">
                                    {searchTerm
                                        ? 'Try adjusting your search.'
                                        : 'Upload a PDF to get started.'}
                                </p>
                                {!searchTerm && (
                                    <Button
                                        variant="primary"
                                        onClick={handleUploadNewResource}
                                        className="mt-2"
                                    >
                                        <AddIcon fontSize="small" className="me-1" />
                                        Upload Resource
                                    </Button>
                                )}
                            </div>
                        ) : (
                            <TanstackTable
                                data={filteredData}
                                columns={columns}
                                pagination={true}
                            />
                        )}
                    </>
                )}
            </div>
            {/* DELETE CONFIRMATION DIALOG */}
<ConfirmationDialog
  open={deleteDialog.open}
  handleClose={closeDeleteDialog}
  handleConfirm={confirmDelete}
  message={`Delete "${deleteDialog.fileName}"? This cannot be undone.`}
  isLoading={deleteDialog.loading}
  confirmText="Delete"
  cancelText="Cancel"
/>
        </>
    );
};